import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { TourEngineInterface } from '../tour.engine.interface.js';

/**
 * Helper to determine if a DOM element is genuinely visible in layout.
 * Accurately catches responsive hiding (e.g. Tailwind `hidden lg:block`), display: none,
 * visibility: hidden, or zero-dimension/detached elements.
 */
export function isElementVisible(el) {
  if (!el || !(el instanceof Element)) return false;

  // 1. Native checkVisibility API (Chrome 105+, Safari 17.4+, Firefox 106+)
  if (typeof el.checkVisibility === 'function') {
    try {
      if (!el.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true })) {
        return false;
      }
    } catch (e) {
      // Fallback if checkVisibility fails in edge cases
    }
  }

  // 2. Client rects check: display: none on element or any ancestor produces 0 rects
  if (typeof el.getClientRects === 'function') {
    const rects = el.getClientRects();
    if (!rects || rects.length === 0) {
      return false;
    }
  }

  // 3. Bounding client rect width/height check
  if (typeof el.getBoundingClientRect === 'function') {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }
  }

  // 4. offsetParent, ancestor dimensions and computed styles check:
  // Walk up ancestor chain to detect display: none, visibility: hidden, or collapsed containers (width/height <= 0)
  if (typeof window !== 'undefined' && window.getComputedStyle) {
    let curr = el;
    while (curr && curr !== document.body && curr !== document.documentElement) {
      const style = window.getComputedStyle(curr);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      if (curr instanceof HTMLElement && curr.offsetParent === null && style.position !== 'fixed') {
        return false;
      }
      if (typeof curr.getBoundingClientRect === 'function') {
        const r = curr.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) {
          return false;
        }
      }
      curr = curr.parentElement;
    }
  }

  return true;
}

/**
 * Driver.js Engine Adapter.
 * Encapsulates driver.js implementation behind the agnostic TourEngineInterface.
 */
export class DriverEngine extends TourEngineInterface {
  constructor() {
    super();
    this.instance = null;
    this._isActive = false;
  }

  init(options = {}) {
    // Options are mapped from agnostic options to driver options
    this.defaultOptions = options;
  }

  drive(steps, options = {}) {
    // If an existing driver instance is alive, cleanly destroy it first
    if (this.instance) {
      try {
        this.instance.destroy();
      } catch (e) {
        console.warn('[DriverEngine] Error destroying existing driver instance:', e);
      }
      this.instance = null;
    }
    this._isActive = false;

    const mergedOptions = { ...this.defaultOptions, ...options };
    const stepMap = new Map();
    this._activeMenu = null;
    this._lastActiveStep = null;

    const cleanupActiveMenu = () => {
      if (this._lastActiveStep?.closeMenu) {
        try {
          if (typeof this._lastActiveStep.closeMenu === 'function') {
            this._lastActiveStep.closeMenu(this._lastActiveStep);
          } else if (typeof this._lastActiveStep.closeMenu === 'string') {
            const btn = document.querySelector(this._lastActiveStep.closeMenu);
            btn?.click?.();
          }
        } catch (e) {
          console.warn('[DriverEngine] Failed to clean up active menu:', e);
        }
      }
      this._activeMenu = null;
      this._lastActiveStep = null;
    };

    // Filter only steps that are truly in DOM, visible, or have openMenu / beforeEnter
    const validSteps = steps
      .map((step, index) => {
        const hasOpenHook = Boolean(step.openMenu || step.beforeEnter);
        let el = null;
        if (typeof step.target === 'function') {
          try {
            el = step.target();
          } catch (e) {
            el = null;
          }
        } else if (typeof step.target === 'string') {
          el = document.querySelector(step.target);
        } else if (step.target instanceof HTMLElement) {
          el = step.target;
        }

        // If not in DOM or not visible (and no open hook to reveal it), drop step immediately
        if (!hasOpenHook && (!el || !isElementVisible(el))) {
          return null;
        }

        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        const resolvedSide = (isMobile && step.position === 'right') ? 'bottom' : (step.position || 'bottom');
        const resolvedAlign = (isMobile && step.position === 'right') ? 'center' : (step.align || 'start');

        const driverStep = {
          element: () => {
            let targetNode = null;
            if (typeof step.target === 'function') {
              try {
                targetNode = step.target();
              } catch (e) {
                targetNode = null;
              }
            } else if (typeof step.target === 'string') {
              targetNode = document.querySelector(step.target);
            } else if (step.target instanceof HTMLElement) {
              targetNode = step.target;
            }

            if (!hasOpenHook && (!targetNode || !isElementVisible(targetNode))) {
              return null;
            }
            return targetNode;
          },
          popover: {
            title: step.title || '',
            description: step.description || '',
            side: resolvedSide,
            align: resolvedAlign,
            showButtons: step.showButtons ?? ['next', 'previous', 'close'],
            nextBtnText: mergedOptions.nextBtnText || 'Next',
            prevBtnText: mergedOptions.prevBtnText || 'Back',
            doneBtnText: mergedOptions.doneBtnText || 'Done'
          },
          onHighlightStarted: async (element, stepRef, hookOpts) => {
            const rawStep = stepRef?.__rawStep
              || (hookOpts?.index !== undefined ? validSteps[hookOpts.index]?.__rawStep : null)
              || this._findRawStep(stepRef, validSteps);

            const resolvedTargetEl = typeof rawStep?.target === 'function'
              ? (() => { try { return rawStep.target(); } catch (e) { return null; } })()
              : (typeof rawStep?.target === 'string' ? document.querySelector(rawStep.target) : element);

            const targetNode = resolvedTargetEl || element;
            const stepHasOpenHook = Boolean(rawStep?.openMenu || rawStep?.beforeEnter);

            // If Driver.js failed to find the target in the DOM (dummy fallback),
            // or if the element is not visible and has no pending open hook, skip step immediately
            if (element?.id === 'driver-dummy-element' || !targetNode || (!stepHasOpenHook && !isElementVisible(targetNode))) {
              if (this.instance?.isLastStep()) {
                this.instance?.destroy();
              } else {
                this.instance?.moveNext();
              }
              return;
            }

            if (rawStep) {
              this._lastActiveStep = rawStep;

              // 1. Open menu or run beforeEnter hook if element was hidden
              if (rawStep.openMenu && this._activeMenu !== rawStep.openMenu) {
                try {
                  if (typeof rawStep.openMenu === 'function') {
                    rawStep.openMenu(rawStep);
                  } else if (typeof rawStep.openMenu === 'string') {
                    const triggerEl = document.querySelector(rawStep.openMenu);
                    triggerEl?.click?.();
                  }
                  this._activeMenu = rawStep.openMenu;

                  // Multiple refreshes as menu animation unfolds
                  setTimeout(() => this.instance?.refresh(), 60);
                  setTimeout(() => this.instance?.refresh(), 180);
                  setTimeout(() => {
                    this.instance?.refresh();
                    const targetEl = element || (typeof rawStep?.target === 'string' ? document.querySelector(rawStep.target) : null);
                    if (targetEl && (rawStep?.scrollIntoView ?? true)) {
                      try {
                        const scrollOpts = typeof rawStep?.scrollIntoView === 'object'
                          ? rawStep.scrollIntoView
                          : { behavior: 'smooth', block: 'center', inline: 'nearest' };
                        targetEl.scrollIntoView?.(scrollOpts);
                      } catch (err) {
                        console.warn('[DriverEngine] Scroll into view failed:', err);
                      }
                    }
                  }, 320);
                } catch (err) {
                  console.warn('[DriverEngine] Error executing openMenu:', err);
                }
              }

              if (typeof rawStep.beforeEnter === 'function') {
                try {
                  await rawStep.beforeEnter(rawStep, { element, hookOpts, driver: this.instance });
                } catch (err) {
                  console.warn('[DriverEngine] Error executing beforeEnter:', err);
                }
              }
            }

            // 2. Scroll element into view smoothly if requested (when not opening menu or immediately)
            if (!rawStep?.openMenu) {
              const targetEl = element || (typeof rawStep?.target === 'function' ? rawStep.target() : typeof rawStep?.target === 'string' ? document.querySelector(rawStep.target) : null);
              if (targetEl && (rawStep?.scrollIntoView ?? true)) {
                try {
                  const scrollOpts = typeof rawStep?.scrollIntoView === 'object'
                    ? rawStep.scrollIntoView
                    : { behavior: 'smooth', block: 'center', inline: 'nearest' };

                  if (typeof targetEl.scrollIntoView === 'function') {
                    targetEl.scrollIntoView(scrollOpts);
                  }
                } catch (err) {
                  console.warn('[DriverEngine] Scroll into view failed:', err);
                }
              }
            }

            // 3. Refresh driver overlay position after possible DOM layout changes
            if (this.instance) {
              this.instance.refresh();
            }

            // 4. Footer buttons setup (Skip button & Done button handling)
            setTimeout(() => {
              try {
                const popoverEl = document.querySelector('.mytriad-tour-popover');
                const footer = popoverEl?.querySelector('.driver-popover-footer');
                if (!footer) return;

                const isLast = (hookOpts?.index !== undefined && hookOpts.index === validSteps.length - 1)
                  || this.instance?.isLastStep?.();

                const existingSkip = footer.querySelector('.driver-popover-skip-btn');
                if (isLast) {
                  // On the last step, remove the skip button since we are finishing
                  existingSkip?.remove();

                  // Ensure the next/done button text and action
                  const nextBtn = footer.querySelector('.driver-popover-next-btn') || footer.querySelector('.driver-popover-done-btn');
                  if (nextBtn) {
                    const doneText = mergedOptions.doneBtnText || mergedOptions.closeBtnText || 'Concluir';
                    nextBtn.innerText = doneText;
                    nextBtn.classList.add('driver-popover-done-btn');
                    nextBtn.onclick = (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this._completedByDoneClick = true;
                      this.destroy();
                    };
                  }
                } else {
                  // Inject explicit "Pular / Skip" button into popover footer if not present
                  if (!existingSkip) {
                    const skipBtn = document.createElement('button');
                    skipBtn.type = 'button';
                    skipBtn.className = 'driver-popover-skip-btn';
                    skipBtn.innerText = mergedOptions.skipBtnText || 'Pular';
                    skipBtn.onclick = (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.destroy();
                    };
                    const prevBtn = footer.querySelector('.driver-popover-prev-btn');
                    const btnContainer = prevBtn?.parentElement || footer;
                    btnContainer.insertBefore(skipBtn, prevBtn || btnContainer.firstChild);
                  }
                }
              } catch (e) {
                // Safe fallback
              }
            }, 30);

            if (mergedOptions.onStepChange) {
              mergedOptions.onStepChange(stepRef, element);
            }
          },
          onDeselected: async (element, stepRef, hookOpts) => {
            const rawStep = stepRef?.__rawStep
              || (hookOpts?.index !== undefined ? validSteps[hookOpts.index]?.__rawStep : null)
              || this._findRawStep(stepRef, validSteps);

            if (rawStep) {
              const nextIndex = hookOpts?.driver?.getActiveIndex?.();
              const nextStep = typeof nextIndex === 'number' ? validSteps[nextIndex] : null;
              const nextRawStep = nextStep?.__rawStep || null;

              // Proactively open menu if next step requires it so it's ready when highlighted
              if (nextRawStep?.openMenu && this._activeMenu !== nextRawStep.openMenu) {
                try {
                  if (typeof nextRawStep.openMenu === 'function') {
                    nextRawStep.openMenu(nextRawStep);
                  } else if (typeof nextRawStep.openMenu === 'string') {
                    const triggerEl = document.querySelector(nextRawStep.openMenu);
                    triggerEl?.click?.();
                  }
                  this._activeMenu = nextRawStep.openMenu;
                } catch (err) {
                  console.warn('[DriverEngine] Error proactively executing openMenu:', err);
                }
              }

              // Close menu if next step does not want the menu open
              if (rawStep.closeMenu && !nextRawStep?.openMenu) {
                try {
                  if (typeof rawStep.closeMenu === 'function') {
                    rawStep.closeMenu(rawStep);
                  } else if (typeof rawStep.closeMenu === 'string') {
                    const closeEl = document.querySelector(rawStep.closeMenu);
                    closeEl?.click?.();
                  }
                } catch (err) {
                  console.warn('[DriverEngine] Error executing closeMenu:', err);
                }
                this._activeMenu = null;
              }

              if (typeof rawStep.afterLeave === 'function') {
                try {
                  await rawStep.afterLeave(rawStep, { element, hookOpts, driver: this.instance });
                } catch (err) {
                  console.warn('[DriverEngine] Error executing afterLeave:', err);
                }
              }
            }
          }
        };

        driverStep.__rawStep = step;
        driverStep.__stepIndex = index;
        stepMap.set(driverStep, step);
        return driverStep;
      })
      .filter(Boolean);

    if (validSteps.length === 0) {
      console.warn('[DriverEngine] No matching DOM elements found for tour steps.');
      return;
    }

    const totalValid = validSteps.length;
    validSteps.forEach((s, idx) => {
      const isLast = idx === totalValid - 1;
      if (isLast) {
        s.popover.nextBtnText = mergedOptions.doneBtnText || mergedOptions.closeBtnText || 'Concluir';
        s.popover.doneBtnText = mergedOptions.doneBtnText || mergedOptions.closeBtnText || 'Concluir';
        s.popover.onNextClick = () => {
          this._completedByDoneClick = true;
          this.destroy();
        };
        s.popover.onDoneClick = () => {
          this._completedByDoneClick = true;
          this.destroy();
        };
      } else {
        s.popover.nextBtnText = mergedOptions.nextBtnText || 'Avançar';
        s.popover.doneBtnText = mergedOptions.doneBtnText || mergedOptions.closeBtnText || 'Concluir';
        s.popover.onNextClick = () => {
          this.instance?.moveNext();
        };
      }
    });

    this._completedByDoneClick = false;
    this._onCompleteCallback = mergedOptions.onComplete || null;

    // If step 0 has openMenu, trigger it immediately so drawer is opening when driver starts
    if (validSteps[0]?.__rawStep?.openMenu) {
      try {
        const firstRaw = validSteps[0].__rawStep;
        if (typeof firstRaw.openMenu === 'function') {
          firstRaw.openMenu(firstRaw);
        } else if (typeof firstRaw.openMenu === 'string') {
          const triggerEl = document.querySelector(firstRaw.openMenu);
          triggerEl?.click?.();
        }
        this._activeMenu = firstRaw.openMenu;
      } catch (err) {
        console.warn('[DriverEngine] Failed to open menu for first step:', err);
      }
    }

    this.instance = driver({
      animate: mergedOptions.animate ?? true,
      overlayColor: mergedOptions.overlayColor || 'rgb(0, 0, 0)',
      overlayOpacity: mergedOptions.overlayOpacity ?? 0.6,
      smoothScroll: mergedOptions.smoothScroll ?? true,
      allowScroll: mergedOptions.allowScroll ?? true,
      waitForElement: mergedOptions.waitForElement ?? 600,
      skipMissingElement: true,
      allowClose: true,
      overlayClickBehavior: mergedOptions.overlayClickBehavior || 'close',
      showProgress: mergedOptions.showProgress ?? true,
      stagePadding: mergedOptions.stagePadding ?? 6,
      stageRadius: mergedOptions.stageRadius ?? 8,
      popoverClass: mergedOptions.popoverClass || 'mytriad-tour-popover',
      nextBtnText: mergedOptions.nextBtnText || 'Avançar',
      prevBtnText: mergedOptions.prevBtnText || 'Voltar',
      doneBtnText: mergedOptions.doneBtnText || mergedOptions.closeBtnText || 'Concluir',
      steps: validSteps,
      onDoneClick: () => {
        this._completedByDoneClick = true;
        this.destroy();
      },
      onDestroyed: (element, step, hookOpts) => {
        cleanupActiveMenu();
        this._isActive = false;
        const reason = hookOpts?.destroyReason || (this._completedByDoneClick ? 'done' : 'close');
        if (reason === 'done' || this._completedByDoneClick) {
          if (mergedOptions.onComplete) {
            try {
              mergedOptions.onComplete();
            } catch (e) {
              // Safe fallback
            }
          }
        } else {
          if (mergedOptions.onClose) {
            try {
              mergedOptions.onClose();
            } catch (e) {
              // Safe fallback
            }
          }
        }
      }
    });

    this._isActive = true;
    this.instance.drive();
  }

  _findRawStep(stepRef, validSteps) {
    if (!stepRef) return null;
    if (stepRef.__rawStep) return stepRef.__rawStep;
    return validSteps?.find(s => s === stepRef || s.element === stepRef.element)?.__rawStep || null;
  }

  next() {
    if (this.instance && this._isActive) {
      if (this.instance.isLastStep()) {
        this._completedByDoneClick = true;
        this.destroy();
      } else {
        this.instance.moveNext();
      }
    }
  }

  previous() {
    if (this.instance && this._isActive) {
      this.instance.movePrevious();
    }
  }

  destroy() {
    if (this._lastActiveStep?.closeMenu) {
      try {
        if (typeof this._lastActiveStep.closeMenu === 'function') {
          this._lastActiveStep.closeMenu(this._lastActiveStep);
        } else if (typeof this._lastActiveStep.closeMenu === 'string') {
          const btn = document.querySelector(this._lastActiveStep.closeMenu);
          btn?.click?.();
        }
      } catch (e) {
        // Safe fallback
      }
    }
    this._activeMenu = null;
    this._lastActiveStep = null;

    if (this.instance) {
      try {
        this.instance.destroy();
      } catch (e) {
        // Safe fallback
      }
      this.instance = null;
    }
    this._isActive = false;

    if (typeof document !== 'undefined') {
      try {
        const leftovers = document.querySelectorAll('.driver-overlay, .driver-popover, .driver-stage, .driver-active-element');
        leftovers.forEach(el => el.remove());
        document.body.classList.remove('driver-active', 'driver-fade', 'driver-simple', 'driver-no-scroll');
      } catch (e) {
        // Safe fallback
      }
    }

    if (this._completedByDoneClick && this._onCompleteCallback) {
      try {
        this._onCompleteCallback();
      } catch (e) {
        console.warn('[DriverEngine] Error executing onComplete callback:', e);
      }
      this._onCompleteCallback = null;
    }
  }

  isActive() {
    return this._isActive && !!this.instance;
  }
}

export default DriverEngine;
